"use client";

import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePWA } from "@/app/hooks/use-pwa";

export interface PWAInstallBannerProps {
  showInstallBanner?: boolean;
  dismissBanner?: () => void;
}

export function PWAInstallBanner({ showInstallBanner: overrideShowInstallBanner, dismissBanner: overrideDismissBanner }: PWAInstallBannerProps = {}) {
  const { showInstallBanner: hookShowInstallBanner, platform, dismissBanner: hookDismissBanner } = usePWA();

  // Use override props if provided, otherwise use hook values
  const showInstallBanner = overrideShowInstallBanner ?? hookShowInstallBanner;
  const dismissBanner = overrideDismissBanner ?? hookDismissBanner;
  const [showInstructions, setShowInstructions] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setShowInstructions(false);
      }
    };

    if (showInstructions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showInstructions]);

  if (!showInstallBanner) return null;

  const handleInstall = async () => {
    // Only show instructions for iOS Safari
    setShowInstructions(!showInstructions);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40">
      {/* Main Banner */}
      <div className="bg-gradient-to-br from-gray-800 via-gray-700 to-purple-600/80 text-white w-full shadow-lg mb-[3rem] py-2 pr-1 relative">
        <div className="flex items-center">
          <Button
            onClick={dismissBanner}
            variant="ghost"
            size="icon"
            className="text-white/80 hover:text-white hover:bg-white/10 h-6 w-6 p-0"
            aria-label="Dismiss install prompt"
          >
            <X size={12} />
          </Button>

          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <img
              src="/icon_beta.png"
              alt="Beta Icon"
              className="h-8 w-auto object-contain"
            />
            <p className="text-xs font-semibold text-white/90 truncate flex-1">
              Want Maximal Immersion?
            </p>
          </div>

          <Button
            onClick={handleInstall}
            variant="secondary"
            size="sm"
            className="bg-white text-purple-600 hover:bg-white/90 font-semibold rounded-full h-7 px-3"
          >
            Add to Home Screen
          </Button>

          {/* Custom Popover */}
          {showInstructions && (
            <div className="absolute bottom-full right-0 mb-2 w-72 bg-gradient-to-br from-gray-900 via-gray-800 to-purple-600/80 rounded-xl shadow-lg border-0 z-50 animate-slide-down">
              <div className="p-4">
                <h4 className="font-semibold text-white mb-3">
                  On iPhone/iPad:
                </h4>

                <ol className="space-y-2">
                  <li className="flex items-start gap-3 text-sm">
                    <span className="text-black bg-white rounded-full w-5 h-5 flex items-center justify-center font-semibold flex-shrink-0 text-xs">
                      1
                    </span>
                    <span className="text-white/90 leading-relaxed">
                      Tap Share
                      <img
                        src="/pwa/ios_share.png"
                        alt="Share icon"
                        className="inline-block w-5 h-5 ml-1 filter brightness-0 invert"
                      /> at the bottom
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <span className="text-black bg-white rounded-full w-5 h-5 flex items-center justify-center font-semibold flex-shrink-0 text-xs">
                      2
                    </span>
                    <span className="text-white/90 leading-relaxed">
                      Tap "Add to Home Screen"
                    </span>
                  </li>
                </ol>
              </div>

              {/* Arrow pointing down */}
              <div className="absolute top-full right-4 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
